import React, { useState, useEffect, useRef } from "react";
import { Bell, X, Loader2, BellRing } from "lucide-react";
import { useInView } from "react-intersection-observer";
import { useSelector } from "react-redux";
import { FaTrash } from "react-icons/fa";
import { pageEventSelector } from "@/store/features/dashboardSlice";
import { useNotifications, useMarkAsRead, useMarkAsClear } from "@/services/notifications.services";
import { convertToLocalDateTime } from "@/utils/helper/moment";
import { Image } from "@/common/ui/Image";
import { handleCustomNavigation } from "@/utils/helper/navigation";
import { useNavigate } from "react-router-dom";

interface Notification {
  id: number;
  title: string;
  description: string;
  timestamp: string;
  isSeen: boolean;
  type: "info" | "success" | "warning" | "error";
}

const Notification: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewedNotifications, setViewedNotifications] = useState<Set<number>>(new Set());
  const [pendingMarkAsRead, setPendingMarkAsRead] = useState<Set<number>>(new Set());
  const [removingNotifications, setRemovingNotifications] = useState<Set<number>>(new Set());
  const notificationTimersRef = useRef<Map<number, NodeJS.Timeout>>(new Map());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasCalledNextPageRef = useRef<boolean>(false);
  const navigate = useNavigate();

  const pageEvent = useSelector(pageEventSelector);
  const { ref: inViewRef, inView } = useInView({
    threshold: 0.1,
  });

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, refetch } = useNotifications(isModalOpen);
  const { mutate: markAsRead, isPending: isMarkingAsRead } = useMarkAsRead();
  const { mutate: markAsClear, isPending: isClearing } = useMarkAsClear();
  const allNotifications = data?.pages.flatMap((page) => page.notifications) ?? [];
  const unreadCount = data?.pages[0]?.count ?? 0;
  const totalCount = data?.pages[0]?.totalCount ?? 0;

  // Reset the hasCalledNextPageRef when not in view
  useEffect(() => {
    if (!inView) {
      hasCalledNextPageRef.current = false;
    }
  }, [inView]);

  // Handle infinite scroll with single call prevention
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && !isLoading && allNotifications.length > 0 && !hasCalledNextPageRef.current) {
      hasCalledNextPageRef.current = true;
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, isLoading, fetchNextPage, allNotifications.length]);

  useEffect(() => {
    if (!isModalOpen) return;

    allNotifications.forEach((notification) => {
      if (!notification.isSeen && !viewedNotifications.has(notification.id) && !removingNotifications.has(notification.id)) {
        setViewedNotifications((prev) => new Set(prev).add(notification.id));

        const timer = setTimeout(() => {
          setPendingMarkAsRead((prev) => new Set(prev).add(notification.id));
          notificationTimersRef.current.delete(notification.id);
        }, 5000);

        notificationTimersRef.current.set(notification.id, timer);
      }
    });

    return () => {
      notificationTimersRef.current.forEach((timer) => clearTimeout(timer));
      notificationTimersRef.current.clear();
    };
  }, [allNotifications, isModalOpen, viewedNotifications, removingNotifications]);

  const handleCloseModal = () => {
    if (pendingMarkAsRead.size > 0) {
      markAsRead(
        {
          ids: Array.from(pendingMarkAsRead),
          mode: "single",
        },
        {
          onSuccess: () => {
            setPendingMarkAsRead(new Set());
          },
        }
      );
    }

    notificationTimersRef.current.forEach((timer) => clearTimeout(timer));
    notificationTimersRef.current.clear();

    setIsModalOpen(false);
    setViewedNotifications(new Set());
  };

  const handleButtonClick = () => {
    setIsModalOpen(true);
  };

  const handleMarkAllAsRead = () => {
    markAsRead(
      {
        ids: [],
        mode: "all",
      },
      {
        onSuccess: () => {
          setPendingMarkAsRead(new Set());
          setViewedNotifications(new Set());
          notificationTimersRef.current.forEach((timer) => clearTimeout(timer));
          notificationTimersRef.current.clear();
        },
      }
    );
  };

  const handleClearSingle = (notificationId: number) => {
    setRemovingNotifications((prev) => new Set(prev).add(notificationId));

    markAsClear(
      { ids: [notificationId], mode: "single" },
      {
        onSuccess: () => {
          setTimeout(() => {
            setRemovingNotifications((prev) => {
              const newSet = new Set(prev);
              newSet.delete(notificationId);
              return newSet;
            });
          }, 300);
        },
        onError: () => {
          setRemovingNotifications((prev) => {
            const newSet = new Set(prev);
            newSet.delete(notificationId);
            return newSet;
          });
        },
      }
    );
  };

  const handleClearAll = () => {
    const allIds = allNotifications.map((n) => n.id);
    setRemovingNotifications(new Set(allIds));

    markAsClear(
      { ids: [], mode: "all" },
      {
        onSuccess: () => {
          setTimeout(() => {
            setPendingMarkAsRead(new Set());
            setViewedNotifications(new Set());
            setRemovingNotifications(new Set());
            notificationTimersRef.current.forEach((timer) => clearTimeout(timer));
            notificationTimersRef.current.clear();
          }, 300);
        },
      }
    );
  };

  const getNotificationBorderColor = (type: Notification["type"]) => {
    switch (type) {
      case "success":
        return "!border-green";
      case "warning":
        return "!border-yellow";
      case "error":
        return "!border-red";
      default:
        return "!border-blue-400";
    }
  };

  useEffect(() => {
    if (pageEvent?.keys.includes("notifications") && pageEvent?.type === "refresh") {
      refetch();
    }
  }, [pageEvent, refetch]);
  useEffect(() => {
    if (isModalOpen) {
      refetch();
    }
  }, [isModalOpen]);

  const visibleNotifications = allNotifications.filter((notification) => !removingNotifications.has(notification.id));

  return (
    <div className="relative">
      <button
        onClick={handleButtonClick}
        className="relative p-1 rounded-lg bg-white outline-none shadow-sm hover:bg-gray-50 hover:shadow-md transition-all duration-200 focus:outline-none group"
      >
        <div className="relative">
          <Bell className="w-6 h-6 text-gray-600 group-hover:text-gray-800 transition-colors duration-200" />

          {(unreadCount > 0 || isLoading) && (
            <div className="absolute -top-2 -right-2 min-w-4 h-4 bg-red-500 rounded-full flex items-center justify-center bg-text-red">
              {isLoading ? (
                <Loader2 className="w-3 h-3 text-white animate-spin" />
              ) : (
                <span className="text-10 font-semibold text-white px-1">{unreadCount > 99 ? "99+" : unreadCount}</span>
              )}
            </div>
          )}
        </div>
      </button>

      {isModalOpen && (
        <>
          <div className="fixed inset-0 bg-black/20 z-40 transition-opacity duration-300" onClick={handleCloseModal} />

          <div className="fixed top-0 right-0 h-full w-96 xl:w-side-modal-xl bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
              <div className="flex items-center gap-1.5">
                <BellRing className="w-5 h-5 text-gray-700" />
                <h2 className="text-lg font-semibold text-text-default">Notifications</h2>
                {visibleNotifications.length > 0 && <span className="text-base pt-0.5 font-semibold text-gray-500">({totalCount})</span>}
              </div>
              <div className="flex items-center gap-2">
                {visibleNotifications.length > 0 && (
                  <button
                    onClick={handleClearAll}
                    disabled={isClearing}
                    className="flex items-center gap-1.5 text-10 text-red hover:opacity-95 font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Clear all
                  </button>
                )}
                <button onClick={handleCloseModal} className="p-1 rounded-full hover:bg-gray-200 transition-colors duration-200">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-3" />
                  <p className="text-gray-500 text-sm">Loading notifications...</p>
                </div>
              ) : visibleNotifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full p-6">
                  <Bell className="w-12 h-12 text-gray-300 mb-3" />
                  <p className="text-gray-500 text-center">No notifications yet</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {allNotifications.map((notification, index) => {
                    const isMarkedForRead = pendingMarkAsRead.has(notification.id);
                    const isRemoving = removingNotifications.has(notification.id);

                    return (
                      <div
                        key={`${notification.id}-${index}`}
                        className={`p-4 hover:bg-gray-50 cursor-pointer border-l-2 !border-r-0 !border-y-0 ${getNotificationBorderColor(notification.type)} ${
                          !notification.isSeen && !isMarkedForRead ? "bg-blue-50/50" : ""
                        } group relative transition-all duration-300 ${isRemoving ? "opacity-0 translate-x-full" : "opacity-100 translate-x-0"}`}
                        style={{ transition: "all 0.3s ease-out" }}
                        onClick={() => {
                          if (!notification.isSeen) {
                            Promise.resolve(markAsRead({ ids: [notification.id], mode: "single" }));
                          }
                          return notification?.redirectLink && handleCustomNavigation(notification.redirectLink, navigate, () => setIsModalOpen(false));
                        }}
                      >
                        <div className="flex gap-3">
                          <div className="flex-shrink-0 mt-1">
                            <Image
                              src={notification?.profilePic ? notification.profilePic : "/images/avatar.png"}
                              alt="Icon"
                              className="h-8 w-8 rounded-full"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <h3
                                className={`text-sm font-medium text-text-default line-clamp-2 ${
                                  !notification.isSeen && !isMarkedForRead ? "font-semibold" : ""
                                }`}
                              >
                                {notification.title}
                              </h3>
                              {!notification.isSeen && !isMarkedForRead && <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1" />}
                            </div>
                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">{notification.description}</p>
                            <p className="text-xs text-gray-400 mt-2">{convertToLocalDateTime(notification.addedTs)}</p>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClearSingle(notification.id);
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-2 text-red-500 hover:text-red-600 bg-white rounded-full border border-red-200 shadow-sm hover:shadow-md"
                        >
                          <FaTrash className="text-sm text-red cursor-pointer size-2.5 md:size-3" />
                        </button>
                      </div>
                    );
                  })}

                  {/* Load More Trigger - Only show when there are more pages */}
                  {hasNextPage && visibleNotifications.length > 0 && (
                    <div ref={inViewRef} className="flex justify-center p-4">
                      {isFetchingNextPage ? <Loader2 className="w-6 h-6 text-blue-500 animate-spin" /> : null}
                    </div>
                  )}
                </div>
              )}
            </div>

            {visibleNotifications.length > 0 && (
              <div className="p-2 bg-gray-50 border-t border-gray-200 flex-shrink-0">
                <button
                  onClick={handleMarkAllAsRead}
                  disabled={isMarkingAsRead}
                  className="w-full py-2 text-sm text-text-primary font-medium hover:bg-gray-100 rounded transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isMarkingAsRead && <Loader2 className="w-4 h-4 animate-spin" />}
                  Mark all as read
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Notification;
